export const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

export const fmtInt = (v) => new Intl.NumberFormat("pt-BR").format(Number(v || 0));

export const fmtPct = (v) => {
  const n = Number(v || 0);
  return `${n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

export const pct = (num, den) => {
  const n = Number(num || 0);
  const d = Number(den || 0);
  if (d <= 0) return 0;
  return (n / d) * 100;
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};

// Extrai apenas dígitos de uma string.
const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

/**
 * Formata como CPF (11 dígitos) ou CNPJ (14 dígitos) conforme a quantidade digitada.
 * Aceita entrada parcial e mantém a máscara evoluindo enquanto o usuário digita.
 * Se tiver > 11 dígitos, aplica máscara de CNPJ; caso contrário, CPF.
 */
export const formatDocumento = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  if (d.length <= 11) {
    // CPF: 000.000.000-00
    return d
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }
  // CNPJ: 00.000.000/0000-00
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

export const documentoTipo = (v) => (onlyDigits(v).length > 11 ? "CNPJ" : "CPF");

// ==== Validação de dígitos verificadores ====

const allSameDigit = (s) => /^(\d)\1+$/.test(s);

/** Valida CPF (algoritmo dos dígitos verificadores). */
export const isValidCPF = (v) => {
  const d = onlyDigits(v);
  if (d.length !== 11) return false;
  if (allSameDigit(d)) return false;
  const calc = (base, factorStart) => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) sum += Number(base[i]) * (factorStart - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  const d1 = calc(d.slice(0, 9), 10);
  const d2 = calc(d.slice(0, 10), 11);
  return d1 === Number(d[9]) && d2 === Number(d[10]);
};

/** Valida CNPJ (algoritmo dos dígitos verificadores). */
export const isValidCNPJ = (v) => {
  const d = onlyDigits(v);
  if (d.length !== 14) return false;
  if (allSameDigit(d)) return false;
  const calcCNPJ = (base, weights) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) sum += Number(base[i]) * weights[i];
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = calcCNPJ(d.slice(0, 12), w1);
  const d2 = calcCNPJ(d.slice(0, 13), w2);
  return d1 === Number(d[12]) && d2 === Number(d[13]);
};

/** Retorna true se for CPF válido, CNPJ válido, ou string vazia (não obrigatório). */
export const isValidDocumento = (v) => {
  const d = onlyDigits(v);
  if (!d) return true; // vazio = ok
  if (d.length === 11) return isValidCPF(d);
  if (d.length === 14) return isValidCNPJ(d);
  return false;
};

export const exportCSV = (filename, rows, headers) => {
  const BOM = "\uFEFF";
  // SEC-004: neutraliza CSV formula injection.
  // Excel/Sheets executam qualquer célula que começa com = + - @ (ou TAB/CR)
  // como fórmula ao abrir o arquivo. Prefixamos com apóstrofo para tratar
  // como texto literal — perde 1 char inicial cosmético em nomes maliciosos,
  // mas remove o vetor de execução no computador de quem abre o export.
  const neutralizeFormula = (s) => {
    if (s.length === 0) return s;
    const first = s.charCodeAt(0);
    // 61 = '=' | 43 = '+' | 45 = '-' | 64 = '@' | 9 = TAB | 13 = CR
    if (first === 61 || first === 43 || first === 45 || first === 64 || first === 9 || first === 13) {
      return "'" + s;
    }
    return s;
  };
  const esc = (val) => {
    if (val === null || val === undefined) return "";
    const raw = neutralizeFormula(String(val));
    const s = raw.replace(/"/g, '""');
    return `"${s}"`;
  };
  const headerLine = headers.map((h) => esc(h.label)).join(";");
  const bodyLines = rows.map((row) => headers.map((h) => esc(row[h.key])).join(";"));
  const csv = BOM + [headerLine, ...bodyLines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const cleanPhone = (p) => String(p || "").replace(/\D/g, "");
